<script setup lang="ts">
import {computed, toRefs} from "vue";
import {dateToString} from "@/utils/dateUtils";
import {useCartStore} from "@/stores/cartStore";
import type {ShippingMethodExact, ShippingMethodGround} from "@/types/shipping.types";
const cartStore = useCartStore();
const properties = defineProps<{
  day: {
    date: Date;
    day: number;
    isDisabled: boolean;
  };
  exactDisabled: boolean;
  hoveredDates: Date[];
  groundDelivery: ShippingMethodGround | null;
  nextDayDelivery: ShippingMethodExact | null;
  secondDayDelivery: ShippingMethodExact | null;
  firstAvailableShippingDate: Date;
  selectedDates: Date[];
  exactDayShippingDate: Date | null;
  rangeShippingDate: Date | null;
  dontDisableSaturday: boolean;
  disabledShippingDates: Date[];
  disabledDeliveryDates: Date[];
  clickedDate: Date | null;
  isExactDayDelivery: boolean;
}>();

const emit = defineEmits(['clickDay', 'mouseIn', 'mouseOut']);

const { day, exactDisabled, hoveredDates, nextDayDelivery, secondDayDelivery, firstAvailableShippingDate, selectedDates, exactDayShippingDate, rangeShippingDate, dontDisableSaturday, disabledShippingDates, disabledDeliveryDates, clickedDate, isExactDayDelivery } = toRefs(properties);

const transformDate = computed(() => {
  return dateToString(day.value.date);
});

const showExtraAmount = computed(() => !isExactDayDelivery.value && !clickedDate.value || isExactDayDelivery.value);

const isPriority = computed(() => nextDayDelivery.value && dateToString(nextDayDelivery.value.day) === transformDate.value || secondDayDelivery.value && dateToString(secondDayDelivery.value.day) === transformDate.value);

const isDateHovered = computed(() => {
  if(!hoveredDates.value) {
    return false;
  }

  return hoveredDates.value.findIndex(i => dateToString(i) === transformDate.value) === 0 || hoveredDates.value.findIndex(i => dateToString(i) === transformDate.value) === hoveredDates.value.length - 1
});

const isDateSelected = computed(() => {
  if(!selectedDates.value.length) {
    return false;
  }

  if(selectedDates.value.length === 2) {
    return dateToString(selectedDates.value[0]) <= transformDate.value && dateToString(selectedDates.value[1]) >= transformDate.value;
  }

  return dateToString(selectedDates.value[0]) === transformDate.value;
});

const isHover = computed(() => {
  if(hoveredDates.value && hoveredDates.value.length !== 2) {
    return false;
  }

  return dateToString(hoveredDates.value[0]) < transformDate.value && dateToString(hoveredDates.value[1]) > transformDate.value;
});

</script>
<template>
  <div
    @click="() => { emit('clickDay', day.date); }"
    @mouseover="() => { emit('mouseIn', day.date); }"
    @mouseleave="() => { emit('mouseOut') }"
    :class="{
      'day': true,
      'day--priority': (!groundDelivery || groundDelivery && !groundDelivery.isOneTwo) && showExtraAmount && isPriority,
      'day--disabled-soft': day.date.getDay() === 6 && dontDisableSaturday,
      'day--disabled': exactDisabled || day.isDisabled || day.date.getDay() === 0 || day.date.getDay() === 6 && !dontDisableSaturday,
      'day--active': selectedDates.length ? isDateSelected : hoveredDates && hoveredDates.length === 2 ? isDateHovered : false,
      'day--is-range': selectedDates.length === 2 && isDateSelected,
      'day--hover': selectedDates.length ? false : isHover,
    }"
  >
    <div class="day__number">
      {{ day.day }}
    </div>
    <template v-if="cartStore.isDebug">
      <div class="day__debug" v-if="rangeShippingDate && selectedDates.length === 2 && dateToString(rangeShippingDate) === transformDate">
        ship day<br> range
      </div>
      <div class="day__debug" v-if="dateToString(firstAvailableShippingDate) === transformDate">
        ship day<br> for<br> 1 and 2
      </div>
      <div class="day__debug" v-if="exactDayShippingDate && dateToString(exactDayShippingDate) === transformDate">
        ship day<br> exact day
      </div>
      <div class="day__debug" v-if="disabledDeliveryDates.findIndex(i => dateToString(i)===transformDate) !== -1">
        is<br> disabled<br> delivery
      </div>
      <div class="day__debug" v-if="disabledShippingDates.findIndex(i => dateToString(i)===transformDate) !== -1">
        is<br> disabled<br> shipping
      </div>
    </template>
    <div class="day__price" v-if="(!groundDelivery || groundDelivery && !groundDelivery.isOneTwo) && showExtraAmount && (nextDayDelivery && dateToString(nextDayDelivery.day) === transformDate || secondDayDelivery && dateToString(secondDayDelivery.day) === transformDate)">
      <template v-if="nextDayDelivery && nextDayDelivery.extra > 0 && dateToString(nextDayDelivery.day) === transformDate">
        +${{ nextDayDelivery.extra }}
      </template>
      <template v-if="(!groundDelivery || groundDelivery && !groundDelivery.isOneTwo) && secondDayDelivery && secondDayDelivery.extra > 0 && dateToString(secondDayDelivery.day) === transformDate">
        +${{ secondDayDelivery.extra }}
      </template>
    </div>
  </div>
</template>